import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Trash2, Loader2, Save } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { useConfig, applyBrand } from "@/context/ConfigContext";
import { API, authed } from "@/lib/api";
import {
  Field, Section, inputCls, AddBtn, ImageField, BrandingSection, ContentSection,
  FacilitiesSection, GallerySection, TestimonialsSection,
} from "@/components/admin/SettingsSections";

export default function Settings() {
  const { schoolId } = useParams();
  const { config, setConfig } = useConfig();
  const { getToken } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (config && !form) setForm(JSON.parse(JSON.stringify(config))); }, [config, form]);
  useEffect(() => { if (form) applyBrand(form); }, [form]);
  useEffect(() => () => applyBrand(config), [config]);

  if (!form) return <AdminShell><p className="text-stone-400">Loading…</p></AdminShell>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCourse = (i, k, v) => set("courses", form.courses.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const setTiming = (i, v) => set("timings", form.timings.map((t, j) => (j === i ? v : t)));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, courses: form.courses.map((c) => ({ ...c, monthly_fee: Number(c.monthly_fee) || 0 })) };
      const { data } = await axios.put(`${API}/schools/${schoolId}/config`, payload, await authed(getToken));
      setConfig(data.config);
      setForm(JSON.parse(JSON.stringify(data.config)));
      toast.success("Saved — website and chat assistant updated");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell>
      <form onSubmit={save} className="space-y-6 pb-24" data-testid="white-label-config-form">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Website &amp; settings</h1>
          <p className="text-sm text-stone-500 mt-1">Everything on this school's website and in its chat assistant comes from here.</p>
        </div>

        <Section title="Identity">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Institute name"><input required className={inputCls} value={form.institute_name} onChange={(e) => set("institute_name", e.target.value)} data-testid="config-input-institute-name" /></Field>
            <Field label="App name"><input className={inputCls} value={form.app_name} onChange={(e) => set("app_name", e.target.value)} data-testid="config-input-app-name" /></Field>
          </div>
          <Field label="Tagline (hero headline)"><input className={inputCls} value={form.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="config-input-tagline" /></Field>
          <Field label="Short description"><textarea rows={3} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="config-input-description" /></Field>
          <div className="grid sm:grid-cols-2 gap-6">
            <ImageField label="Logo" value={form.logo_url} onChange={(v) => set("logo_url", v)} kind="logo" schoolId={schoolId} testid="config-logo" />
            <ImageField label="Hero banner" value={form.hero_image_url} onChange={(v) => set("hero_image_url", v)} kind="banner" schoolId={schoolId} testid="config-banner" />
          </div>
        </Section>

        <BrandingSection form={form} set={set} />
        <ContentSection form={form} set={set} />

        <Section title="Classes & monthly fees" hint="Fees are in INR (₹) per month. The chat assistant quotes only these.">
          {form.courses.map((c, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`config-course-row-${i}`}>
              <input className={`${inputCls} col-span-12 sm:col-span-4`} placeholder="Class name" value={c.name} onChange={(e) => setCourse(i, "name", e.target.value)} data-testid={`config-course-name-${i}`} />
              <input className={`${inputCls} col-span-6 sm:col-span-4`} placeholder="Age group" value={c.age_group} onChange={(e) => setCourse(i, "age_group", e.target.value)} data-testid={`config-course-age-${i}`} />
              <div className="col-span-5 sm:col-span-3 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-stone-400">₹</span>
                <input type="number" min="0" className={`${inputCls} pl-7`} value={c.monthly_fee} onChange={(e) => setCourse(i, "monthly_fee", e.target.value)} data-testid={`config-input-fees-${i}`} />
              </div>
              <button type="button" onClick={() => set("courses", form.courses.filter((_, j) => j !== i))} className="col-span-1 p-2 text-stone-400 hover:text-red-600" data-testid={`config-course-remove-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <AddBtn onClick={() => set("courses", [...form.courses, { name: "", age_group: "", monthly_fee: 0 }])} label="Add class" testid="config-add-course-button" />
        </Section>

        <Section title="Timings">
          {form.timings.map((t, i) => (
            <div key={i} className="flex gap-2">
              <input className={inputCls} value={t} placeholder="e.g. Morning Batch: 9:00 AM – 12:00 PM" onChange={(e) => setTiming(i, e.target.value)} data-testid={`config-timing-${i}`} />
              <button type="button" onClick={() => set("timings", form.timings.filter((_, j) => j !== i))} className="p-2 text-stone-400 hover:text-red-600" data-testid={`config-timing-remove-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <AddBtn onClick={() => set("timings", [...form.timings, ""])} label="Add timing" testid="config-add-timing-button" />
        </Section>

        <FacilitiesSection form={form} set={set} />
        <GallerySection form={form} set={set} schoolId={schoolId} />
        <TestimonialsSection form={form} set={set} />

        <Section title="Contact details">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Phone number"><input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="config-input-phone" /></Field>
            <Field label="Email"><input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="config-input-email" /></Field>
          </div>
          <Field label="Address"><textarea rows={2} className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="config-input-address" /></Field>
        </Section>

        <div className="fixed bottom-0 inset-x-0 bg-white/90 backdrop-blur border-t z-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex justify-end">
            <button type="submit" disabled={saving} className="flex items-center gap-2 px-6 py-2.5 rounded-full btn-brand font-semibold text-sm disabled:opacity-60" data-testid="save-config-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save settings
            </button>
          </div>
        </div>
      </form>
    </AdminShell>
  );
}
